import {
  adminService,
  couponService,
  customerService,
  analyticsService,
  logService,
} from "@/services";
import { ApiRoutes } from "@/enums/apiRoutes.enum";
import { CustomerRank, CustomerRfmSegment } from "@/enums/customer.enum";
import { useQueryParams } from "@/hooks/useQueryParams";
import axiosInstance from "@/lib/axios";
import { createClient } from "@/lib/supabaseClient";
import { CustomerStats, Transaction } from "@/types/data";
import { handleErrorLog } from "@/utils/misc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { User } from "@supabase/supabase-js";
import { createCustomerQueryParamsObject } from "@/hooks/utils";

const supabase = createClient();

// Labels for Client-Facing Alerts
const labels = {
  notAdmin: "Korisnik nije administrator. Kontaktirajte podršku.",
  otpSendFailed: "Slanje OTP-a nije uspjelo. Molimo pokušajte ponovno.",
  otpNotRequired: "Korisniku nije potreban OTP. Nevažeći tijek.",
  otpVerifyFailed: "Verifikacija OTP-a nije uspjela. Molimo pokušajte ponovno.",
  otpSessionSavedFailed: "Pristupni podaci nisu ispravno pohranjeni.",
  passwordUpdateFailed:
    "Ažuriranje lozinke nije uspjelo. Molimo pokušajte ponovno.",
  passwordUpdateSuccess: "Lozinka uspješno ažurirana!",
  loginSuccess: "Uspješna prijava!",
  loginError: "Pogreška prilikom prijave. Molimo pokušajte ponovno.",
};

export const useAdmin = (email: string) => {
  return useQuery({
    queryKey: ["admin", email],
    queryFn: () => adminService.checkAdmin(email),
    enabled: !!email,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useUser = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data } = await axiosInstance.get(ApiRoutes.ME);

      return data.user as User;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export interface Customer {
  spol?: "ž" | "m";
  gender?: "ž" | "m";
  kvart: string; // Neighborhood
  barkod: string; // Barcode
  godina_rodenja: number; // Year of Birth
  totalSpend: string;
  totalPoints: number;
  numberOfTransactions: number;
  numberOfUsedCoupons: number;
  numberOfBoughtCoupons: number;
  username: string;
  email: string;
  fcm_token?: string;
  first_name?: string | null;
  surname?: string | null;
  date_of_birth?: string | null;
  vip?: boolean;
  rfm?: CustomerRfmSegment;
  rang?: CustomerRank;
}

export const usePostCouponMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => couponService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availableCoupons"] });
    },
  });
};

export const useDeleteCouponMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[] | number) => couponService.delete(ids),
    onSuccess: () => {
      console.log("Coupon deleted successfully");

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["availableCoupons"] });
      }, 500);
    },
  });
};

export const useUpdateCouponMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => couponService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availableCoupons"] });
    },
  });
};

export const useCustomerStats = (
  barcodes: string[] | undefined,
  config: Record<string, any>,
) => {
  const { query } = useQueryParams();

  const queryObject = createCustomerQueryParamsObject(query, barcodes);

  return useQuery<CustomerStats>({
    queryKey: ["customerStats", queryObject],
    queryFn: () => customerService.getStats(queryObject),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    ...config,
  });

  // this is old before barcodes were supported
  // const queryString = createCustomerQueryParams(query, undefined);

  // return useQuery<CustomerStats>({
  //   queryKey: ["customerStats", queryString],
  //   queryFn: () => fetchData(`${ApiRoutes.CUSTOMERS_STATS}?${queryString}`),
  //   staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  //   retry: false,
  //   refetchOnWindowFocus: true,
  //   refetchOnReconnect: true,
  //   ...config,
  // });
};

// Hook to fetch transactions
export const useTransactions = (customerId?: string) => {
  return useQuery<Transaction[], unknown, Record<string, any>[]>({
    queryKey: ["transactions", customerId],
    queryFn: () => analyticsService.getTransactions(customerId),
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useSendOTPMutation = (props?: Record<string, any>) => {
  return useMutation({
    mutationFn: async (email: string) => {
      // Fetch admin data
      const adminData = await adminService.checkAdmin(email);

      if (!adminData.isAdmin) {
        throw new Error(labels.notAdmin);
      }

      if (adminData.isAdmin && adminData.passwordChanged === false) {
        console.log(
          "✅ The user is an administrator and the password is not set - sending OTP",
        );

        const { error } = await supabase.auth.signInWithOtp({
          email,
        });

        if (error) {
          handleErrorLog(error);

          throw new Error(labels.otpSendFailed);
        }

        console.log("✅ OTP sent successfully");

        return { success: true };
      }

      throw new Error(labels.otpNotRequired);
    },
    ...props,
  });
};

interface VerifyOTPVariables {
  email: string;
  otp: string;
}

export const useVerifyOTPMutation = (props?: Record<string, any>) => {
  return useMutation({
    mutationFn: async ({ email, otp }: VerifyOTPVariables) => {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: "email",
      });

      if (error) {
        handleErrorLog(error);

        throw new Error(labels.otpVerifyFailed);
      }

      const { session } = data;

      if (!session || !session.access_token || !session.refresh_token) {
        throw new Error(labels.otpVerifyFailed);
        return;
      }

      try {
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
      } catch (error) {
        console.log("error:", error);
        throw new Error(labels.otpSessionSavedFailed);
      }

      console.log("✅ OTP successfully verified:", data);

      setTimeout(() => {
        window.location.href = "/password-reset";
      }, 1000);

      return data;
    },
    ...props,
  });
};

export const useUpdatePasswordMutation = (props?: Record<string, any>) => {
  return useMutation({
    mutationFn: async (password: string) => {
      const { data, error } = await supabase.auth.updateUser({ password });

      if (error) {
        handleErrorLog(error);

        throw new Error(labels.passwordUpdateFailed);
      }

      console.log("✅ Password updated!:", data);

      return { success: true };
    },
    ...props,
  });
};

export const useLoginMutation = (props?: Record<string, any>) => {
  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      const response = await axiosInstance.post(
        ApiRoutes.AUTH_LOGIN,
        { email, password },
        { withCredentials: true },
      );

      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("response:", JSON.stringify(response.data));

      // Session cookies are set by the login response above, so the usage-log
      // route can see the session. Fire-and-forget — never block the login.
      logService.logUsage("login", "/login").catch(() => {});

      return { success: true };
    },
    ...props,
  });
};

export const useLoginMutation2 = (props?: Record<string, any>) => {
  return useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const { email, password } = data;
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new Error(labels.loginError);
      }

      // if (data.session) {
      //   document.cookie = `supabase-auth=${JSON.stringify(
      //     data.session
      //   )}; path=/; secure; HttpOnly; SameSite=Lax`;
      // }

      // artificial delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      return { success: true };
    },
    ...props,
  });
};

export const useIsAdmin = () => {
  const { data: user } = useUser();
  const { data } = useAdmin(user?.email ?? "");
  return !!data;
};

export const useRole = () => {
  const { data: user } = useUser();
  const { data } = useAdmin(user?.email ?? "");
  const role = data?.role ?? null;
  return role;
};
